import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const AREA_META = {
  health:        { label: 'Health',        color: '#4caf7d' },
  mind:          { label: 'Mind',          color: '#9b5ce0' },
  career:        { label: 'Career',        color: '#c9a84c' },
  relationships: { label: 'Relationships', color: '#e05c5c' },
  faith:         { label: 'Faith',         color: '#5c8fe0' },
  purpose:       { label: 'Goals',       color: '#e07d3c' },
}

function getLocalDate() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

export default function CoachPage({ onNavigate }) {
  const { user, profile } = useAuth()
  const [loading, setLoading] = useState(false)
  const [coaching, setCoaching] = useState(null)
  const [error, setError] = useState(null)
  const [todayScores, setTodayScores] = useState({})
  const isPro = true // everyone has access
  const firstName = profile?.full_name?.split(' ')[0] || 'there'
  const isSunday = new Date().getDay() === 0

  // Weekly report state
  const [weeklyLoading, setWeeklyLoading] = useState(false)
  const [weeklyReport, setWeeklyReport] = useState(null)
  const [weeklyError, setWeeklyError] = useState(null)
  const [weekData, setWeekData] = useState([])

  useEffect(() => {
    if (user) {
      loadTodayScores()
      if (isSunday) loadWeekData()
    }
  }, [user])

  async function loadWeekData() {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 6)
    const startStr = `${start.getFullYear()}-${String(start.getMonth()+1).padStart(2,'0')}-${String(start.getDate()).padStart(2,'0')}`
    const { data } = await supabase
      .from('manual_tasks')
      .select('area, final_points, date')
      .eq('user_id', user.id)
      .gte('date', startStr)
      .eq('flagged', false)
    setWeekData(data || [])
  }

  async function loadTodayScores() {
    const { data } = await supabase
      .from('manual_tasks')
      .select('area, final_points')
      .eq('user_id', user.id)
      .eq('date', getLocalDate())
      .eq('flagged', false)
    if (data) {
      const pts = {}
      data.forEach(t => { pts[t.area] = (pts[t.area] || 0) + (t.final_points || 0) })
      setTodayScores(pts)
    }
  }

  async function getCoaching() {
    setLoading(true)
    setError(null)
    setCoaching(null)

    const scores = todayScores
    const lowestAreas = Object.entries(AREA_META)
      .map(([key, meta]) => ({ key, label: meta.label, score: scores[key] || 0 }))
      .sort((a, b) => a.score - b.score)
      .slice(0, 2)
      .map(a => a.label)

    const prompt = `You are a direct, no-nonsense life coach inside the LifeScore app. Tell the user EXACTLY what to do to raise their scores. Be specific, actionable, and direct. No fluff.

User: ${firstName}
Today's area scores (points earned from tasks logged):
- Health: ${scores.health || 0} pts
- Mind: ${scores.mind || 0} pts
- Career: ${scores.career || 0} pts
- Relationships: ${scores.relationships || 0} pts
- Faith: ${scores.faith || 0} pts
- Purpose: ${scores.purpose || 0} pts
Lowest areas: ${lowestAreas.join(' and ')}

Give a coaching response in this EXACT JSON format with no other text:
{
  "headline": "one punchy sentence summarizing their day (max 12 words)",
  "overall_verdict": "2-3 sentences on their scores and what they mean",
  "actions": [
    {
      "area": "area name",
      "score": 0,
      "priority": "high|medium|low",
      "action": "one specific thing to do RIGHT NOW (1 sentence, very specific)"
    }
  ],
  "closing": "one motivating sentence to end (direct and personal)"
}`

    try {
      const res = await fetch('/api/coach', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt })
      })
      const data = await res.json()
      if (data.error) { setError('API error: ' + data.error); setLoading(false); return }
      const clean = data.text.replace(/```json|```/g, '').trim()
      setCoaching(JSON.parse(clean))
    } catch (e) {
      setError('Something went wrong. Try again.')
    }
    setLoading(false)
  }

  async function getWeeklyReport() {
    setWeeklyLoading(true)
    setWeeklyError(null)
    setWeeklyReport(null)

    // Aggregate weekly scores per area
    const weeklyTotals = {}
    const dayScores = {}
    weekData.forEach(t => {
      weeklyTotals[t.area] = (weeklyTotals[t.area] || 0) + (t.final_points || 0)
      if (!dayScores[t.date]) dayScores[t.date] = 0
      dayScores[t.date] += (t.final_points || 0)
    })
    const daysActive = Object.keys(dayScores).length
    const avgDailyScore = daysActive ? Math.round(Object.values(dayScores).reduce((a,b) => a+b,0) / daysActive) : 0
    const bestArea = Object.entries(weeklyTotals).sort((a,b) => b[1]-a[1])[0]?.[0] || 'none'
    const weakestArea = Object.entries(AREA_META)
      .map(([k]) => ({ k, pts: weeklyTotals[k] || 0 }))
      .sort((a,b) => a.pts - b.pts)[0]?.k || 'none'

    const prompt = `You are a direct life coach giving a Sunday weekly recap inside the LifeScore app. Be personal, encouraging but honest, and specific.

User: ${firstName}
Week summary:
- Days active (logged tasks): ${daysActive}/7
- Average daily score: ${avgDailyScore} pts
- Area totals this week: ${Object.entries(AREA_META).map(([k,v]) => `${v.label}: ${weeklyTotals[k] || 0} pts`).join(', ')}
- Strongest area: ${bestArea}
- Weakest area: ${weakestArea}

Give a weekly coaching response in this EXACT JSON format with no other text:
{
  "headline": "punchy week summary sentence (max 12 words)",
  "summary": "2-3 sentences reviewing their week honestly",
  "strength": "one sentence celebrating their best area",
  "focus": "one specific thing to prioritize next week",
  "closing": "one motivating sentence to start the new week"
}`

    try {
      const res = await fetch('/api/coach', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt })
      })
      const data = await res.json()
      if (data.error) { setWeeklyError('API error: ' + data.error); setWeeklyLoading(false); return }
      const clean = data.text.replace(/```json|```/g, '').trim()
      setWeeklyReport(JSON.parse(clean))
    } catch (e) {
      setWeeklyError('Something went wrong. Try again.')
    }
    setWeeklyLoading(false)
  }


  // ── ALL USERS GET ACCESS ──
  return (
    <div style={s.page}>
      <div style={s.ambient} />

      <div style={s.header}>
        <div style={s.backBtn} onClick={() => onNavigate('dashboard')}>←</div>
        <div style={s.headerTitle}>AI Coach</div>
        <div style={{ width: 36 }} />
      </div>

      <div style={s.main}>

        <div style={s.introCard}>
          <div style={s.introEyebrow}>Powered by Claude AI</div>
          <div style={s.introText}>
            Get specific, actionable steps to raise your score — based on your tasks logged today.
          </div>
          {!coaching && !loading && (
            <div style={s.coachBtn} onClick={getCoaching}>Analyze My Day →</div>
          )}
        </div>

        {loading && (
          <div style={s.loadingCard}>
            <div style={s.loadingDots}>
              <div style={{ ...s.dot, animationDelay: '0s' }} />
              <div style={{ ...s.dot, animationDelay: '0.2s' }} />
              <div style={{ ...s.dot, animationDelay: '0.4s' }} />
            </div>
            <div style={s.loadingText}>Analyzing your scores...</div>
          </div>
        )}

        {error && (
          <div style={s.errorCard}>
            <div style={s.errorText}>{error}</div>
            <div style={s.retryBtn} onClick={getCoaching}>Try Again</div>
          </div>
        )}

        {coaching && (
          <>
            <div style={s.headlineCard}>
              <div style={s.headlineEyebrow}>Today's Verdict</div>
              <div style={s.headline}>"{coaching.headline}"</div>
              <div style={s.verdict}>{coaching.overall_verdict}</div>
            </div>

            <div style={s.section}>
              <div style={s.sectionTitle}>Your Action Plan</div>
              <div style={s.actionList}>
                {coaching.actions?.map((item, i) => {
                  const meta = AREA_META[item.area?.toLowerCase()] || { color: '#c9a84c' }
                  return (
                    <div key={i} style={s.actionCard}>
                      <div style={{ ...s.actionTop, borderLeftColor: meta.color }}>
                        <div style={s.actionAreaRow}>
                          <div style={{ ...s.actionArea, color: meta.color }}>{item.area}</div>
                          <div style={{
                            ...s.priorityBadge,
                            background: item.priority === 'high' ? 'rgba(224,92,92,0.12)' : item.priority === 'medium' ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.06)',
                            color: item.priority === 'high' ? '#e05c5c' : item.priority === 'medium' ? '#c9a84c' : '#6b6860',
                          }}>{item.priority}</div>
                        </div>
                        <div style={s.actionScore}>Score: <span style={{ color: meta.color, fontFamily: "'Bebas Neue', sans-serif", fontSize: '16px' }}>{item.score}</span></div>
                      </div>
                      <div style={s.actionText}>{item.action}</div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div style={s.closingCard}>
              <div style={s.closingText}>"{coaching.closing}"</div>
            </div>

            <div style={s.reanalyzeBtn} onClick={getCoaching}>Re-analyze →</div>
          </>
        )}

        {/* ── WEEKLY REPORT — Sundays only ── */}
        {isSunday && (
          <div style={s.weeklySection}>
            <div style={s.weeklyHeader}>
              <div style={s.weeklyEyebrow}>📅 Weekly Report</div>
              <div style={s.weeklySubtitle}>Your week wraps up today, {firstName}.</div>
            </div>
            {!weeklyReport && !weeklyLoading && (
              <div style={s.weeklyBtn} onClick={getWeeklyReport}>Get My Weekly Report →</div>
            )}
            {weeklyLoading && (
              <div style={s.loadingCard}>
                <div style={s.loadingDots}>
                  <div style={{ ...s.dot, animationDelay: '0s' }} />
                  <div style={{ ...s.dot, animationDelay: '0.2s' }} />
                  <div style={{ ...s.dot, animationDelay: '0.4s' }} />
                </div>
                <div style={s.loadingText}>Reviewing your week...</div>
              </div>
            )}
            {weeklyError && (
              <div style={s.errorCard}>
                <div style={s.errorText}>{weeklyError}</div>
                <div style={s.retryBtn} onClick={getWeeklyReport}>Try Again</div>
              </div>
            )}
            {weeklyReport && (
              <div style={s.weeklyCard}>
                <div style={s.weeklyHeadline}>"{weeklyReport.headline}"</div>
                <div style={s.weeklyBody}>{weeklyReport.summary}</div>
                <div style={s.weeklyDivider} />
                <div style={s.weeklyRow}>
                  <div style={s.weeklyRowLabel}>💪 Strength</div>
                  <div style={s.weeklyRowText}>{weeklyReport.strength}</div>
                </div>
                <div style={s.weeklyRow}>
                  <div style={s.weeklyRowLabel}>🎯 Next Week</div>
                  <div style={s.weeklyRowText}>{weeklyReport.focus}</div>
                </div>
                <div style={s.weeklyClosing}>"{weeklyReport.closing}"</div>
                <div style={s.reanalyzeBtn} onClick={getWeeklyReport}>Refresh →</div>
              </div>
            )}
          </div>
        )}

      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  )
}

const s = {
  page: { minHeight: '100vh', background: '#111118', color: '#e8e6df', fontFamily: "'DM Sans', sans-serif", position: 'relative', overflowX: 'hidden' },
  ambient: { position: 'fixed', top: '-30%', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '800px', background: 'radial-gradient(ellipse, rgba(201,168,76,0.22) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 },
  header: { position: 'sticky', top: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 'max(18px, env(safe-area-inset-top))', paddingBottom: '18px', paddingLeft: '24px', paddingRight: '24px', background: 'rgba(6,6,8,0.85)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.12)' },
  backBtn: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.06)', borderRadius: '10px', cursor: 'pointer', fontSize: '18px', color: '#c9a84c' },
  headerTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '3px', color: '#e8e6df' },
  main: { maxWidth: '420px', margin: '0 auto', padding: '24px 20px 100px', position: 'relative', zIndex: 1 },
  upsellWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', position: 'relative', zIndex: 1 },
  upsellCard: { background: '#1a1a26', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '20px', padding: '32px 24px', textAlign: 'center', maxWidth: '360px', width: '100%' },
  upsellEmoji: { fontSize: '48px', marginBottom: '16px' },
  upsellTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '3px', color: '#f0cc7a', marginBottom: '12px' },
  upsellText: { fontSize: '14px', color: '#6b6860', lineHeight: '1.6', marginBottom: '20px' },
  upsellFeatures: { textAlign: 'left', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '8px' },
  upsellFeat: { fontSize: '13px', color: '#e8e6df' },
  upsellPrice: { fontSize: '13px', color: '#c9a84c', fontWeight: '500', marginBottom: '16px' },
  upsellBtn: { padding: '14px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', borderRadius: '12px', cursor: 'pointer', fontSize: '15px', fontWeight: '700', marginBottom: '10px' },
  upsellDismiss: { fontSize: '13px', color: '#6b6860', cursor: 'pointer', padding: '8px' },
  introCard: { background: 'linear-gradient(135deg, rgba(201,168,76,0.08), rgba(201,168,76,0.03))', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '16px', padding: '22px 20px', marginBottom: '24px' },
  introEyebrow: { fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#c9a84c', marginBottom: '10px' },
  introText: { fontSize: '14px', color: '#e8e6df', lineHeight: '1.6', marginBottom: '16px' },
  coachBtn: { display: 'inline-block', padding: '13px 20px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', fontWeight: '700', fontSize: '14px', borderRadius: '12px', cursor: 'pointer', letterSpacing: '0.5px' },
  loadingCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px', padding: '40px 20px', textAlign: 'center', marginBottom: '24px' },
  loadingDots: { display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '16px' },
  dot: { width: '8px', height: '8px', borderRadius: '50%', background: '#c9a84c', animation: 'pulse 1.2s ease-in-out infinite' },
  loadingText: { fontSize: '13px', color: '#6b6860' },
  errorCard: { background: 'rgba(224,92,92,0.08)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '16px', padding: '20px', marginBottom: '24px', textAlign: 'center' },
  errorText: { fontSize: '14px', color: '#e05c5c', marginBottom: '12px' },
  retryBtn: { display: 'inline-block', padding: '10px 20px', background: 'rgba(224,92,92,0.15)', color: '#e05c5c', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  headlineCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px', padding: '22px 20px', marginBottom: '24px' },
  headlineEyebrow: { fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '10px' },
  headline: { fontFamily: "'DM Serif Display', serif", fontStyle: 'italic', fontSize: '20px', lineHeight: '1.4', color: '#f0cc7a', marginBottom: '12px' },
  verdict: { fontSize: '14px', color: '#e8e6df', lineHeight: '1.6' },
  section: { marginBottom: '24px' },
  sectionTitle: { fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '12px' },
  actionList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  actionCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', overflow: 'hidden' },
  actionTop: { padding: '14px 16px 10px', borderLeft: '3px solid', display: 'flex', flexDirection: 'column', gap: '4px' },
  actionAreaRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  actionArea: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '15px', letterSpacing: '1.5px' },
  priorityBadge: { fontSize: '10px', fontWeight: '600', padding: '3px 8px', borderRadius: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  actionScore: { fontSize: '12px', color: '#6b6860' },
  actionText: { padding: '10px 16px 14px', fontSize: '14px', color: '#e8e6df', lineHeight: '1.55', borderTop: '1px solid rgba(255,255,255,0.05)' },
  closingCard: { background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '14px', padding: '18px 20px', marginBottom: '16px' },
  closingText: { fontFamily: "'DM Serif Display', serif", fontStyle: 'italic', fontSize: '16px', lineHeight: '1.6', color: '#e8e6df' },
  reanalyzeBtn: { textAlign: 'center', fontSize: '13px', color: '#c9a84c', cursor: 'pointer', padding: '8px', marginBottom: '8px' },
  weeklySection: { marginTop: '32px', borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: '28px' },
  weeklyHeader: { marginBottom: '16px' },
  weeklyEyebrow: { fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: '#c9a84c', marginBottom: '6px' },
  weeklySubtitle: { fontSize: '14px', color: '#6b6860' },
  weeklyBtn: { display: 'inline-block', padding: '13px 20px', background: 'linear-gradient(135deg, #5c8fe0, #2d5fa0)', color: '#fff', fontWeight: '700', fontSize: '14px', borderRadius: '12px', cursor: 'pointer', letterSpacing: '0.5px', marginBottom: '8px' },
  weeklyCard: { background: '#1a1a26', border: '1px solid rgba(92,143,224,0.2)', borderRadius: '16px', padding: '22px 20px' },
  weeklyHeadline: { fontFamily: "'DM Serif Display', serif", fontStyle: 'italic', fontSize: '20px', lineHeight: '1.4', color: '#f0cc7a', marginBottom: '12px' },
  weeklyBody: { fontSize: '14px', color: '#e8e6df', lineHeight: '1.6', marginBottom: '16px' },
  weeklyDivider: { height: '1px', background: 'rgba(255,255,255,0.12)', marginBottom: '16px' },
  weeklyRow: { marginBottom: '12px' },
  weeklyRowLabel: { fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '4px' },
  weeklyRowText: { fontSize: '14px', color: '#e8e6df', lineHeight: '1.5' },
  weeklyClosing: { fontFamily: "'DM Serif Display', serif", fontStyle: 'italic', fontSize: '15px', color: '#c9a84c', lineHeight: '1.6', marginTop: '16px', marginBottom: '12px', borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: '16px' },
}
